/* Delivery requests are optional. Checkout waits for a successful attribute save. */
if (!customElements.get('cart-delivery')) {
  customElements.define('cart-delivery', class extends HTMLElement {
    connectedCallback() {
      this.rules = window.DhuniDeliveryCalendar;
      this.date = this.querySelector('[data-delivery-date]');
      this.note = this.querySelector('[data-order-note]');
      this.review = this.querySelector('[data-order-review]');
      this.wrapping = this.querySelector('[data-order-wrapping]');
      this.time = this.querySelector('[data-delivery-time]');
      this.status = this.querySelector('[data-delivery-status]');
      this.message = this.querySelector('[data-delivery-message]');
      this.form = document.getElementById(this.dataset.form);
      this.abort = new AbortController();
      this.pending = Promise.resolve();
      this.addEventListener('change', () => {
        this.pending = this.pending.catch(() => {}).then(async () => {
          await this.ready;
          await this.save();
        });
        this.pending.catch((error) => { this.status.textContent = error.message; });
      }, { signal: this.abort.signal });
      this.form.addEventListener('submit', (event) => this.checkout(event), { signal: this.abort.signal });
      this.ready = this.initialize();
      this.ready.catch((error) => { this.status.textContent = error.message; });
      window.addEventListener('pageshow', (event) => {
        if (!event.persisted) return;
        this.ready = this.initialize();
        this.ready.catch((error) => { this.status.textContent = error.message; });
      }, { signal: this.abort.signal });
      if (typeof subscribe === 'function') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
          this.pending = this.pending.catch(() => {}).then(async () => {
            await this.ready;
            await this.save();
          });
          this.pending.catch((error) => { this.status.textContent = error.message; });
        });
      }
    }

    disconnectedCallback() { this.abort.abort(); this.unsubscribe?.(); }

    async json(url, options = {}) {
      try {
        const response = await fetch(url, { ...options, signal: AbortSignal.timeout(12000) });
        if (!response.ok) throw new Error();
        const value = await response.json();
        if (value.errors) throw new Error();
        return value;
      } catch (_) {
        throw new Error('ご希望を確認・保存できませんでした。通信を確認し、もう一度購入手続きへ進んでください。');
      }
    }

    async initialize() {
      // Each visit requires fresh choices; saved attributes may belong to an earlier checkout.
      this.date.value = '';
      this.time.value = '指定なし';
      this.note.value = '';
      this.review.value = 'no';
      this.wrapping.value = 'no';
      await this.save();
      this.note.disabled = false;
      this.review.disabled = false;
      this.wrapping.disabled = false;
    }

    updateAvailability(cart) {
      this.eligible = cart.currency === 'JPY' && cart.total_price >= 1000000 &&
        cart.items.some((item) => item.requires_shipping);
      this.querySelector('[data-delivery-fields]').hidden = !this.eligible;
      this.date.disabled = !this.eligible;
      this.time.disabled = !this.eligible;
      this.date.min = this.rules.minimumDate();
      this.date.removeAttribute('max');
      this.message.textContent = this.eligible
        ? ''
        : '10,000円以上のご購入で日時指定が可能です。\nその場合は最短で発送します。';
      this.querySelector('[data-delivery-guidance]').hidden = !this.eligible;
      const oldDate = this.date.value;
      const oldTime = this.time.value;
      if (!this.eligible) {
        this.date.value = '';
        this.time.value = '指定なし';
      } else if (this.date.value && this.date.value < this.date.min) {
        this.date.value = '';
      }
      return oldDate !== this.date.value || oldTime !== this.time.value;
    }

    async save() {
      const cart = await this.json(`${window.Shopify.routes.root}cart.js`);
      const cleared = this.updateAvailability(cart);
      if (this.date.value && !this.date.checkValidity()) throw new Error('選択できる日付をカレンダーからお選びください。');
      this.status.textContent = 'ご希望を保存中…';
      const attributes = {
        '希望到着日': this.date.value,
        '希望到着時間帯': this.time.value || '指定なし',
        '日時指定の郵便番号': '',
        review_gift_requested: '',
        'レビュー特典': '',
        'レビューのお願い': this.review.value === 'yes' ? '受け取る' : '希望しない',
        'プレゼント包装': this.wrapping.value === 'yes' ? '希望する' : '希望しない',
      };
      await this.json(`${window.Shopify.routes.root}cart/update.js`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes, note: this.note.value }),
      });
      this.status.textContent = cleared
        ? '配送条件が変わったため、お届け希望日時を指定なしに戻しました。ご確認ください。'
        : 'ご希望を保存しました。';
      return !cleared;
    }

    async checkout(event) {
      if (this.allowSubmit || (event.submitter && event.submitter.name !== 'checkout')) return;
      event.preventDefault();
      if (this.submitting) return;
      this.submitting = true;
      try {
        // Retry initialization after a transient failure, and retry any failed save.
        try { await this.ready; } catch (_) { this.ready = this.initialize(); await this.ready; }
        await this.pending.catch(() => {});
        if (!await this.save()) return;
        this.allowSubmit = true;
        this.form.requestSubmit(event.submitter || this.form.querySelector('[name="checkout"]') || document.getElementById('checkout'));
        this.allowSubmit = false;
      } catch (error) {
        this.status.textContent = error.message;
      } finally { this.submitting = false; }
    }
  });
}
